import { format, isSameDay } from 'date-fns'
import { formatHeatPct } from '../../utils/portfolioHeat'
import { StandDownStreak, standDownLabel } from './engineActivity'

/**
 * The figures behind the gate, from the same facts the diagnostics panel
 * reads: open count and heat from the track-record summary (diagnostics'
 * open_book copies them from it), the open-position cap from the admin-only
 * diagnostics facts. Anything missing is left out, never filled in.
 */
export interface StandDownFacts {
  openCount: number | null
  /** MAX_OPEN_PLATFORM_SIGNALS; null for viewers who can't read the diagnostics. */
  maxOpen: number | null
  /** Fractions, as served (0.26 = 26%). */
  heatPct: number | null
  maxHeatPct: number | null
}

const clock = (at: Date, now: Date) => (isSameDay(at, now) ? format(at, 'HH:mm') : format(at, 'MMM d, HH:mm'))

const gateFigure = (reason: string, facts: StandDownFacts): string | null => {
  if (reason === 'max_open_platform_signals') {
    if (facts.openCount == null) return null
    return facts.maxOpen != null ? `${facts.openCount} of ${facts.maxOpen}` : `${facts.openCount} open`
  }
  if (reason === 'portfolio_heat_cap') {
    if (facts.heatPct == null || facts.maxHeatPct == null) return null
    return `${formatHeatPct(facts.heatPct)} of ${formatHeatPct(facts.maxHeatPct, 0)}`
  }
  return null
}

export const standDownMessage = (streak: StandDownStreak, facts: StandDownFacts, now = new Date()): string => {
  const figure = gateFigure(streak.reason, facts)
  return (
    `No new signals: ${standDownLabel(streak.reason)} reached${figure ? ` (${figure})` : ''}` +
    ` — every scan since ${clock(streak.since, now)} has stood down. Last scan ${clock(streak.lastScan, now)}.`
  )
}

/**
 * One plain-English line at the top of the Dashboard while the scan keeps
 * standing down for the same reason, in place of reading it off a column of
 * identical feed rows.
 */
const EngineStandDownBanner = ({ streak, facts }: { streak: StandDownStreak; facts: StandDownFacts }) => (
  <div
    role="status"
    className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200"
    data-testid="stand-down-banner"
    data-reason={streak.reason}
  >
    {standDownMessage(streak, facts)}
  </div>
)

export default EngineStandDownBanner
